<template>
  <!-- The whole recipient page is a SOBER zone (riso NOTES §3): no stamps,
       no pencil, no jokes. It reads as a kind adult, never a mascot. -->
  <main class="page reach">
    <p v-if="loading" class="lab">Opening your page…</p>

    <div v-if="error" class="panel sober err" role="alert">
      <p class="sm">{{ error }}</p>
    </div>

    <template v-if="!loading && recipient">
      <span class="lab">Your page · no account, nothing to sign up for</span>
      <h1 class="d2 head">{{ who }} would like to send you things. You decide what&#8217;s welcome.</h1>
      <p class="lede">
        Hello{{ firstName ? ` ${firstName}` : '' }}. Set whatever you like below. It takes effect the moment you save it,
        and it answers for you every time anyone tries to reach you here. <strong>{{ whoPossessive }} assistant is told only
        what it needs to follow your rules</strong> — never your address, and never what you already own.
      </p>

      <div class="panel sober state">
        <p class="sm" v-if="shutNow"><strong>Right now, the answer is no.</strong> {{ shutReason }}</p>
        <p class="sm" v-else-if="recipient.status === 'claimed'"><strong>This page is yours.</strong> Whenever anyone tries to send you something through us, it gets the answer in the box beside this.</p>
        <p class="sm" v-else><strong>Nothing here is set yet.</strong> Until you save something, the people who already send you things carry on as they do now, under our ordinary limits. Saving anything makes this page yours.</p>
      </div>

      <div class="recip">
        <div class="recip-fields">

          <fieldset>
            <legend class="lab">1 · What you&#8217;ll take</legend>
            <p class="sm mut intro">Tap anything you&#8217;d genuinely be pleased to find. Leave the rest.</p>
            <div class="chiprow">
              <button v-for="k in kinds" :key="k" class="chip" type="button" :aria-pressed="accepts(k) ? 'true' : 'false'" :disabled="saving" @click="toggleKind(k)">
                <span class="box"></span>{{ kindLabel(k) }}
              </button>
            </div>
            <!-- S13: what they'd rather be given. Their assistant holds every gift idea to it. -->
            <span class="fl subq">And when someone wants to give you something</span>
            <div class="chiprow">
              <button v-for="o in substanceOptions" :key="o.key" class="chip" type="button" :aria-pressed="policy.substance === o.key ? 'true' : 'false'" :disabled="saving" @click="policy.substance = o.key">
                <span class="box"></span>{{ o.label }}
              </button>
            </div>
            <p class="xs">{{ substanceHint }}</p>
          </fieldset>

          <fieldset>
            <legend class="lab">2 · How much</legend>
            <p class="howmuch">No more than
              <label class="sr" for="cap">Things a month</label>
              <select id="cap" class="inline-sel" v-model="capChoice" :disabled="saving">
                <option v-for="o in capOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
              a month, from everyone put together.</p>
            <p class="xs">Not per sender. If three people all have you, they share the number.</p>
          </fieldset>

          <fieldset>
            <legend class="lab">3 · Quiet, if you want it</legend>
            <label class="fl" for="quiet">Nothing at all until this date</label>
            <div class="row">
              <input type="date" id="quiet" v-model="quietUntilDate" :disabled="saving" class="date" />
              <button v-if="quietUntilDate" class="btn quiet" type="button" @click="quietUntilDate = ''">Clear</button>
            </div>
            <div class="chiprow gap">
              <button class="chip" type="button" :aria-pressed="policy.status === 'paused' ? 'true' : 'false'" :disabled="saving || policy.status === 'closed'" @click="togglePaused">
                <span class="box"></span>Pause everything, with no end date
              </button>
            </div>
            <p class="xs">Leave it blank unless you want silence. Nothing is held against you for using it.</p>
          </fieldset>

          <fieldset>
            <legend class="lab">4 · Things anyone sending should know</legend>
            <label class="fl" for="constraints">Allergies, things you can&#8217;t have in the house, things you just don&#8217;t want. One per line.</label>
            <textarea id="constraints" v-model="constraintsText" rows="3" placeholder="No nuts — an allergy, not a preference.&#10;Nothing that needs a wall." :disabled="saving"></textarea>
            <div class="chiprow gap">
              <button v-for="q in quickConstraints" :key="q.label" class="chip" type="button" :disabled="saving || hasConstraint(q.line)" @click="addConstraint(q.line)">{{ q.label }}</button>
            </div>
            <p class="xs">A sender&#8217;s assistant is told these word for word, and has to follow them.</p>
          </fieldset>

          <fieldset>
            <legend class="lab">5 · Gift ideas</legend>
            <p class="sm intro">
              When someone&#8217;s assistant is choosing a gift for you, yours can look over the shortlist first and quietly rule out
              what won&#8217;t work: something you already have, or something on your list above. It answers yes or no to each idea,
              and only says more if you choose below. They never learn what you own, and nothing is bought without the sender&#8217;s own say-so.
            </p>
            <div class="chiprow">
              <button class="chip" type="button" :aria-pressed="policy.negotiable ? 'true' : 'false'" :disabled="saving" @click="policy.negotiable = !policy.negotiable">
                <span class="box"></span>Let my assistant rule on gift ideas
              </button>
            </div>
            <div class="steer-choice" :class="{ dim: !policy.negotiable }">
              <span class="fl">How much may it say?</span>
              <div class="chiprow">
                <button class="chip" type="button" :aria-pressed="!steering ? 'true' : 'false'" :disabled="saving" @click="policy.disclosure = 'verdict_only'">
                  <span class="box"></span>Just yes or no
                </button>
                <button class="chip" type="button" :aria-pressed="steering ? 'true' : 'false'" :disabled="saving" @click="policy.disclosure = 'steer'">
                  <span class="box"></span>Yes or no, and a nudge in a direction
                </button>
              </div>
              <p class="xs">
                A nudge is picked from a short fixed list — &#8220;leans toward something to read; less for the kitchen&#8221; — and
                that is all it can say. It never says what you have or what you wrote below. It will also say when an idea is
                &#8220;already coming from someone else&#8221; (never who), which at just yes or no it keeps to itself.
              </p>
              <template v-if="steering">
                <label class="fl" for="taste-into">Lately you&#8217;re into…</label>
                <textarea id="taste-into" v-model="taste.into" maxlength="400" rows="2" placeholder="Long walks with a podcast. Learning to bake bread. Anything about birds." :disabled="saving"></textarea>
                <label class="fl" for="taste-enough">You&#8217;ve had enough of…</label>
                <textarea id="taste-enough" v-model="taste.enough" maxlength="400" rows="2" placeholder="Mugs. Anything that needs dusting. Novelty socks." :disabled="saving"></textarea>
                <p class="xs">
                  Only your own assistant reads these. It uses them to say &#8220;would rather not&#8221; to an idea, or that one
                  fits, and to pick the nudge. Nobody sending is ever shown them.
                </p>
              </template>
            </div>
            <div class="holdings" :class="{ dim: !policy.negotiable }">
              <label class="fl" for="holdings">Things you already have, so nobody gives you a second. One per line — name the object.</label>
              <textarea id="holdings" v-model="holdingsText" rows="4" placeholder="a good coffee grinder&#10;more candles than I&#8217;ll ever burn&#10;a Kindle" :disabled="saving"></textarea>
              <ul v-if="holdings.length" class="ev read">
                <li v-for="(h, i) in holdings" :key="i">
                  <span><b>{{ h.title }}</b></span>
                  <span v-if="h.matchable" class="meta">read as {{ categoryLabel(h.category) }}</span>
                  <span v-else class="meta unread">can&#8217;t tell what this is</span>
                </li>
              </ul>
              <p v-if="unreadCount" class="xs">
                {{ unreadCount === 1 ? 'One line' : `${unreadCount} lines` }} won&#8217;t rule anything out: your assistant couldn&#8217;t tell what kind of
                thing {{ unreadCount === 1 ? 'it is' : 'they are' }}. Naming the object helps (&#8220;mug&#8221;, &#8220;scarf&#8221;, &#8220;board game&#8221;).
              </p>
            </div>
            <div class="pooling" :class="{ dim: !policy.negotiable }">
              <div class="chiprow">
                <button class="chip" type="button" :aria-pressed="policy.brokerPooling ? 'true' : 'false'" :disabled="saving" @click="policy.brokerPooling = !policy.brokerPooling">
                  <span class="box"></span>Let people who are both getting me something go in together
                </button>
              </div>
              <p class="xs">
                On unless you turn it off. When two people are each choosing you something for the same birthday, and both want to, your
                assistant can put them together &mdash; one better gift instead of two near-misses. You never hear about it: not that it
                happened, not who. Neither of them learns the other exists unless both say yes, and then only a first name.
              </p>
            </div>
          </fieldset>

          <fieldset>
            <legend class="lab">6 · In your own words</legend>
            <label class="fl" for="note">One line, passed on word for word to anyone&#8217;s assistant that asks.</label>
            <textarea id="note" class="theirs" v-model="policy.note" maxlength="280" rows="2" placeholder="Honestly, a postcard now and then is perfect. Don&#8217;t spend money on me." :disabled="saving"></textarea>
          </fieldset>

          <fieldset>
            <legend class="lab">7 · Where it goes</legend>
            <p class="sm mut intro">
              This replaces whatever address anyone has for you. They don&#8217;t see it — it goes on the label and nowhere else.
              US addresses only for now. Skip it if you&#8217;ve only ticked email.
            </p>
            <p v-if="recipient.ownAddress" class="sm onfile">
              On file: {{ recipient.ownAddress.street }}<span v-if="recipient.ownAddress.optional">, {{ recipient.ownAddress.optional }}</span>,
              {{ recipient.ownAddress.city }}, {{ recipient.ownAddress.state }} {{ recipient.ownAddress.postalCode }}
            </p>
            <div class="addr">
              <div><label class="fl" for="ad-name">Name on the envelope</label><input type="text" id="ad-name" v-model="address.name" :disabled="saving" autocomplete="name" /></div>
              <div><label class="fl" for="ad-street">Street</label><input type="text" id="ad-street" v-model="address.street" :disabled="saving" autocomplete="address-line1" /></div>
              <div><label class="fl" for="ad-opt">Apt, unit (optional)</label><input type="text" id="ad-opt" v-model="address.optional" :disabled="saving" autocomplete="address-line2" /></div>
              <div><label class="fl" for="ad-city">City</label><input type="text" id="ad-city" v-model="address.city" :disabled="saving" autocomplete="address-level2" /></div>
              <div><label class="fl" for="ad-state">State</label><input type="text" id="ad-state" v-model="address.state" maxlength="20" :disabled="saving" autocomplete="address-level1" /></div>
              <div><label class="fl" for="ad-zip">ZIP</label><input type="text" id="ad-zip" v-model="address.postalCode" maxlength="10" :disabled="saving" autocomplete="postal-code" /></div>
            </div>
            <div class="btnrow gap">
              <button class="btn ghost" type="button" @click="claim" :disabled="saving || !addressComplete">
                {{ recipient.ownAddress ? 'Update my address' : 'Save this address' }}
              </button>
            </div>
          </fieldset>

          <fieldset>
            <legend class="lab">8 · Who has sent you things</legend>
            <p v-if="!recipient.senders.length && !events.length" class="sm mut intro">Nobody has sent you anything through us yet.</p>
            <ul v-if="recipient.senders.length" class="ev">
              <li v-for="s in recipient.senders" :key="s.key">
                <span><b>{{ s.name }}</b> — {{ s.count }} so far<template v-if="s.lastAt">, last {{ ago(s.lastAt) }}</template>.</span>
                <button class="btn red small" type="button" :disabled="saving" @click="toggleBlock(s.key, !isBlocked(s.key))">
                  {{ isBlocked(s.key) ? `Unblock ${s.name}` : `Block ${s.name}` }}
                </button>
              </li>
            </ul>
            <template v-if="events.length">
              <span class="lab sub">What&#8217;s happened</span>
              <ul class="ev">
                <li v-for="e in events" :key="e.id">
                  <span>
                    {{ describe(e) }}
                    <span v-if="canReact(e)" class="react">
                      <button v-for="r in reactions" :key="r.key" class="chip tiny" type="button" :aria-pressed="reactionFor(e) === r.key ? 'true' : 'false'" :disabled="saving" @click="react(e, r.key)" :title="r.label">
                        <span aria-hidden="true">{{ r.emoji }}</span><span class="sr">{{ r.label }}</span>
                      </button>
                    </span>
                    <span v-else-if="e.type === 'reaction'" class="mut"> {{ reactionEmoji(e.reaction) }}<span v-if="e.note" class="theirwords"> &#8220;{{ e.note }}&#8221;</span></span>
                  </span>
                  <time>{{ ago(e.at) }}</time>
                </li>
              </ul>
            </template>
            <p class="xs gap">This is most of what the page does: it says no on your behalf, all day, without asking you.</p>
          </fieldset>

          <fieldset v-if="recipient.status === 'claimed'">
            <legend class="lab">9 · For other people&#8217;s assistants</legend>
            <p class="sm intro">
              Your handle is <code class="num">{{ recipient.handle }}</code>. Give it to anyone whose assistant should be able to ask yours.
              They still never see your address.
            </p>
            <div class="chiprow">
              <button class="chip" type="button" :aria-pressed="policy.discoverable ? 'true' : 'false'" :disabled="saving" @click="policy.discoverable = !policy.discoverable">
                <span class="box"></span>Let assistants of people who don&#8217;t already know me ask
              </button>
            </div>
          </fieldset>

          <div class="panel sober">
            <span class="lab">If you&#8217;d rather not, at all</span>
            <template v-if="policy.status === 'closed'">
              <p class="sm gap">Closed. Nothing reaches you from anyone through us, and nobody is told why. Open it again whenever you like.</p>
              <div class="btnrow gap"><button class="btn ghost" type="button" :disabled="saving" @click="setStatus('open')">Open it again</button></div>
            </template>
            <template v-else>
              <p class="sm gap">One button, no questions, no follow-up email. Nothing reaches you from anyone through us until you come back here and open it again. Nobody is told why.</p>
              <div class="btnrow gap"><button class="btn red" type="button" :disabled="saving" @click="setStatus('closed')">Nothing from anyone, ever</button></div>
            </template>
          </div>

          <p class="xs fine">
            This link is private to you. Anyone with it can change these choices, so don&#8217;t share it.
            Questions: <router-link to="/contact-us">contact us</router-link>.
          </p>
        </div>

        <aside class="mirror panel sober" aria-live="polite">
          <div class="mirror-body">
            <span class="lab">Your side, right now</span>
            <p class="theirwords mirror-text">{{ mirrorText }}</p>
            <span class="lab sub">What a sender&#8217;s assistant is told</span>
            <ul class="told sm">
              <li v-for="(t, i) in told" :key="i">{{ t }}</li>
            </ul>
            <span class="lab sub">What stays with you</span>
            <p class="sm">Your address, the things you already have{{ steering ? ', what you wrote about your taste' : '' }}, and this link.</p>
          </div>
          <div class="mirror-foot">
            <button class="btn wide" type="button" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save — takes effect now' }}</button>
            <p v-if="savedAt" class="xs saved" role="status">Saved. It answers for you from now on.</p>
            <p v-else class="xs">Change any of it whenever you like. {{ who === 'Someone you know' ? 'Nobody' : who }} is never told that you did.</p>
          </div>
        </aside>
      </div>
    </template>
  </main>
</template>

<script>
import { getRecipientAgent, claimRecipientAgent, setRecipientPolicy, setRecipientHoldings, setRecipientTaste, reactToRecipientDelivery } from '@/api';

const KIND_LABELS = {
  email: 'Email',
  postcard: 'Postcards',
  note: 'Handwritten notes',
  letter: 'Printed letters',
  gift: 'Gifts',
  zine: 'A book about you',
};

const CATEGORY_LABELS = {
  drinkware: 'a mug, cup or bottle', candle: 'a candle', jewelry: 'jewelry', book: 'a book', apparel: 'clothing',
  art_print: 'art for the wall', zine: 'a little book', plant: 'a plant', food: 'food or drink', stationery: 'stationery',
  tech: 'a gadget', home: 'something for the home', game: 'a game or puzzle', bath: 'bath and body', craft: 'a craft kit',
  pet: 'something for a pet',
};

const QUICK_CONSTRAINTS = [
  { label: 'Nut allergy', line: 'No nuts — an allergy, not a preference.' },
  { label: 'No food', line: 'Nothing perishable.' },
  { label: 'I rent', line: 'I rent — nothing that needs a wall.' },
  { label: 'No candles', line: 'No candles.' },
];

// Mirrors SUBSTANCE in functions/shared/recipient/policySchema.js.
const SUBSTANCE_OPTIONS = [
  { key: 'any', label: 'Anything thoughtful', hint: 'Whatever they think you’d like, within the rest of this page.' },
  { key: 'fewer_things', label: 'Fewer things, more words', hint: 'Their assistant is told you’d rather have a letter, a note, a little book about you, something that gets used up, or their time. Yours puts any idea for another object to keep at the back of the list. You never have to say it to anyone.' },
  { key: 'words', label: 'Words, not things', hint: 'Letters, notes, postcards, a little book about you, or their time. Yours says no to anything else, every time, and nobody has to have the conversation.' },
];

const listJoin = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
const fmtDate = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

export default {
  name: 'RecipientPage',
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      savedAt: null,
      recipient: null,
      introducedBy: null,
      events: [],
      kinds: [],
      reactions: [
        { key: 'heart', emoji: '❤️', label: 'Loved it' }, { key: 'smile', emoji: '🙂', label: 'Made me smile' },
        { key: 'thanks', emoji: '🙏', label: 'Thank you' }, { key: 'meh', emoji: '😐', label: 'Meh' }, { key: 'no_more', emoji: '✋', label: 'No more of these' },
      ],
      policy: {
        status: 'open', accepts: [], rejects: [], maxPerMonth: null, quietUntil: null, perSender: {}, constraints: [], note: '',
        discoverable: false, negotiable: false, disclosure: 'verdict_only', notifyOnConsult: false, brokerPooling: true, substance: 'any',
      },
      capChoice: '',
      quietUntilDate: '',
      constraintsText: '',
      holdings: [],
      holdingsText: '',
      taste: { into: '', enough: '' },
      savedTaste: { into: '', enough: '' },
      savedHoldingsText: '',
      quickConstraints: QUICK_CONSTRAINTS,
      substanceOptions: SUBSTANCE_OPTIONS,
      address: { name: '', street: '', optional: '', city: '', state: '', postalCode: '' },
    };
  },
  computed: {
    token() {
      // index.html moves the token from the URL into sessionStorage before any
      // script runs; the param is only present if that inline script did not.
      let stored = null;
      try {
        stored = window.sessionStorage.getItem('tou.recipientToken');
      } catch (e) {
        stored = null;
      }
      return this.$route.params.token || stored || '';
    },
    who() {
      const first = this.recipient && this.recipient.senders && this.recipient.senders.find((s) => s.name && s.name !== 'Someone');
      return this.introducedBy || (first && first.name.split(/\s+/)[0]) || 'Someone you know';
    },
    whoPossessive() {
      return this.who === 'Someone you know' ? 'Their' : `${this.who}’s`;
    },
    firstName() {
      const n = this.recipient && this.recipient.name;
      return n ? n.trim().split(/\s+/)[0] : '';
    },
    capOptions() {
      const base = [1, 2, 3, 5, 10];
      const cur = Number.parseInt(this.capChoice, 10);
      const values = Number.isFinite(cur) && !base.includes(cur) ? [...base, cur].sort((a, b) => a - b) : base;
      return [...values.map((v) => ({ value: String(v), label: v === 1 ? '1 thing' : `${v} things` })), { value: '', label: 'no limit' }];
    },
    constraintLines() {
      return this.constraintsText.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 20).map((s) => s.slice(0, 80));
    },
    quietUntilMs() {
      return this.quietUntilDate ? new Date(`${this.quietUntilDate}T00:00:00Z`).getTime() : null;
    },
    shutNow() {
      if (!this.recipient || this.recipient.status !== 'claimed') return false;
      const p = this.recipient.policy || {};
      return p.status === 'closed' || p.status === 'paused' || (p.quietUntil && p.quietUntil > Date.now()) || !(p.accepts || []).length;
    },
    shutReason() {
      const p = (this.recipient && this.recipient.policy) || {};
      if (p.status === 'closed') return 'Nothing reaches you from anyone, and that stays true until you open it again.';
      if (p.status === 'paused') return 'Everything is paused until you open it again.';
      if (p.quietUntil && p.quietUntil > Date.now()) return `Nothing reaches you until ${fmtDate(p.quietUntil)}.`;
      return 'You haven’t said yes to anything, so nothing reaches you. That’s a completely fine answer.';
    },
    acceptedLabels() {
      return this.kinds.filter((k) => this.accepts(k)).map((k) => this.kindLabel(k).toLowerCase());
    },
    // The mirror: the form as it stands (saved or not), restated in plain English.
    mirrorText() {
      const p = this.policy;
      if (p.status === 'closed') return 'Nothing reaches you, from anyone. That’s a completely fine answer.';
      if (p.status === 'paused') return 'Nothing for now. Open it again whenever you like.';
      if (!this.acceptedLabels.length) return 'Nothing reaches you. That’s a completely fine answer, and it’s the one we’ll give until you change it.';
      const parts = [];
      const things = listJoin(this.acceptedLabels);
      parts.push(`${things.charAt(0).toUpperCase()}${things.slice(1)} ${this.acceptedLabels.length === 1 ? 'is' : 'are'} welcome`);
      const cap = Number.parseInt(this.capChoice, 10);
      let first = Number.isFinite(cap) ? `${parts[0]}, ${cap === 1 ? 'one thing' : `${cap} things`} a month at most, from everyone together.` : `${parts[0]}.`;
      if (this.quietUntilMs && this.quietUntilMs > Date.now()) first = `Nothing until ${fmtDate(this.quietUntilMs)}. After that: ${first.charAt(0).toLowerCase()}${first.slice(1)}`;
      const out = [first];
      if (this.accepts('gift') && p.negotiable) out.push('When someone’s assistant is choosing you a gift, yours looks over the shortlist first.');
      if (this.constraintLines.length) out.push(`Anyone sending has to respect ${this.constraintLines.length === 1 ? 'one thing' : `${this.constraintLines.length} things`} on your list.`);
      if (p.substance === 'fewer_things') out.push('Fewer things, more words.');
      if (p.substance === 'words') out.push('Words, not things.');
      return out.join(' ');
    },
    // Exactly what resolveRecipientAddress hands a sender's agent, in words.
    told() {
      const p = this.policy;
      const out = [];
      if (p.status !== 'open') out.push('That nothing can be sent right now.');
      else out.push(this.acceptedLabels.length ? `What you’ll take: ${listJoin(this.acceptedLabels)}.` : 'That you haven’t said yes to anything.');
      if (this.quietUntilMs && this.quietUntilMs > Date.now()) out.push(`That it’s quiet until ${fmtDate(this.quietUntilMs)}.`);
      if (this.constraintLines.length) out.push(`Your list, word for word (${this.constraintLines.length}).`);
      if (p.note && p.note.trim()) out.push('Your line, word for word.');
      if (p.substance && p.substance !== 'any') out.push(p.substance === 'words' ? 'That you’d rather have words than things, as a rule.' : 'That you’d rather have fewer things and more words.');
      if (p.negotiable && this.accepts('gift')) {
        out.push(this.steering ?
          'A yes or no on each gift idea, sometimes a direction from a short fixed list, and when something is already coming from someone else (not who).' :
          'A yes or no on each gift idea, and nothing else.');
      }
      return out;
    },
    steering() {
      return this.policy.disclosure === 'steer';
    },
    substanceHint() {
      const o = SUBSTANCE_OPTIONS.find((x) => x.key === this.policy.substance) || SUBSTANCE_OPTIONS[0];
      return o.hint;
    },
    tasteChanged() {
      return this.taste.into.trim() !== this.savedTaste.into.trim() || this.taste.enough.trim() !== this.savedTaste.enough.trim();
    },
    unreadCount() {
      return this.holdings.filter((h) => !h.matchable).length;
    },
    addressComplete() {
      const a = this.address;
      return a.street.trim() && a.city.trim() && a.state.trim() && /^\d{5}(-\d{4})?$/.test(a.postalCode.trim());
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    kindLabel(k) {
      return KIND_LABELS[k] || k;
    },
    categoryLabel(c) {
      return CATEGORY_LABELS[c] || c;
    },
    accepts(k) {
      return (this.policy.accepts || []).includes(k);
    },
    toggleKind(k) {
      this.policy.accepts = this.accepts(k) ? this.policy.accepts.filter((x) => x !== k) : [...this.policy.accepts, k];
    },
    togglePaused() {
      this.policy.status = this.policy.status === 'paused' ? 'open' : 'paused';
    },
    hasConstraint(line) {
      return this.constraintLines.includes(line);
    },
    addConstraint(line) {
      if (this.hasConstraint(line)) return;
      this.constraintsText = [...this.constraintLines, line].join('\n');
    },
    describe(e) {
      const who = e.senderName || 'Someone';
      const kind = (KIND_LABELS[e.kind] || e.kind || 'something').toLowerCase();
      switch (e.type) {
        case 'intent': return `${who}'s assistant${e.agent ? ` (${e.agent})` : ''} asked what may reach you${e.occasion ? ` for ${e.occasion}` : ''}${e.message ? `: "${e.message}"` : '.'}`;
        case 'offer_accepted': return `Your assistant said yes to ${kind} from ${who}.`;
        case 'offer_declined': return `Your assistant said no to ${kind} from ${who} (${String(e.reason || '').replace(/_/g, ' ')}).`;
        case 'ordered': return `${who} sent ${kind}. It’s on its way.`;
        case 'delivered': return e.title ? `${who} sent you ${e.title}.` : `${who} sent you ${kind}.`;
        case 'reaction': return `You reacted to ${kind} from ${who}.`;
        default: return e.type;
      }
    },
    canReact(e) {
      return e.type === 'ordered' || e.type === 'delivered';
    },
    reactionFor(e) {
      const r = this.events.find((x) => x.type === 'reaction' && (x.refEventId ? x.refEventId === e.id
        : (e.tokenId && x.tokenId === e.tokenId) || (!e.tokenId && x.at > e.at && x.senderName === e.senderName && x.kind === e.kind)));
      return r ? r.reaction : null;
    },
    reactionEmoji(key) {
      const r = this.reactions.find((x) => x.key === key);
      return r ? r.emoji : '';
    },
    async react(e, reaction) {
      this.saving = true;
      this.error = null;
      try {
        const { data } = await reactToRecipientDelivery({ token: this.token, eventId: e.id, reaction });
        this.events = data.events || this.events;
      } catch (err) {
        this.error = (err && err.message) || 'Could not save your reaction.';
      } finally {
        this.saving = false;
      }
    },
    ago(ts) {
      const days = Math.round((Date.now() - ts) / 86400000);
      return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
    },
    isBlocked(key) {
      const rule = this.policy.perSender && this.policy.perSender[key];
      return Boolean(rule && rule.status === 'closed');
    },
    async toggleBlock(key, blocked) {
      const before = this.policy.perSender || {};
      const perSender = { ...before };
      if (blocked) perSender[key] = { ...(perSender[key] || {}), status: 'closed' };
      else if (perSender[key]) {
        const { status, ...rest } = perSender[key]; // eslint-disable-line no-unused-vars
        if (Object.keys(rest).length) perSender[key] = rest; else delete perSender[key];
      }
      this.policy.perSender = perSender;
      const ok = await this.savePolicy();
      // A failed save must not leave them looking blocked while the server still allows them.
      if (!ok) this.policy.perSender = before;
    },
    async setStatus(status) {
      const before = this.policy.status;
      this.policy.status = status;
      const ok = await this.savePolicy();
      if (!ok) this.policy.status = before;
    },
    applyRecipient(recipient) {
      this.recipient = recipient;
      const p = recipient.policy || {};
      this.policy = {
        status: p.status || 'open',
        accepts: Array.isArray(p.accepts) ? [...p.accepts] : [...this.kinds],
        rejects: Array.isArray(p.rejects) ? [...p.rejects] : [],
        maxPerMonth: typeof p.maxPerMonth === 'number' ? p.maxPerMonth : null,
        quietUntil: p.quietUntil || null,
        perSender: p.perSender || {},
        constraints: Array.isArray(p.constraints) ? [...p.constraints] : [],
        note: p.note || '',
        discoverable: Boolean(p.discoverable),
        // Every save sends the whole policy: a field left out would reset to
        // its default (negotiable → false), so carry all of them through.
        negotiable: Boolean(p.negotiable),
        disclosure: p.disclosure || 'verdict_only',
        notifyOnConsult: Boolean(p.notifyOnConsult),
        brokerPooling: p.brokerPooling !== false,
        substance: p.substance || 'any',
      };
      this.capChoice = typeof p.maxPerMonth === 'number' ? String(p.maxPerMonth) : '';
      this.quietUntilDate = p.quietUntil ? new Date(p.quietUntil).toISOString().slice(0, 10) : '';
      this.constraintsText = this.policy.constraints.join('\n');
      const t = recipient.taste || {};
      this.taste = { into: t.into || '', enough: t.enough || '' };
      this.savedTaste = { ...this.taste };
      if (recipient.name && !this.address.name) this.address.name = recipient.name;
    },
    applyHoldings(holdings) {
      this.holdings = holdings;
      this.holdingsText = holdings.map((h) => h.title).join('\n');
      this.savedHoldingsText = this.holdingsText;
    },
    async load() {
      this.loading = true;
      this.error = null;
      if (!this.token) {
        this.error = 'Open this page from the link in something you received.';
        this.loading = false;
        return;
      }
      try {
        const { data } = await getRecipientAgent({ token: this.token });
        this.kinds = data.kinds || [];
        this.events = data.events || [];
        this.introducedBy = data.introducedBy || null;
        this.applyRecipient(data.recipient);
        this.applyHoldings(data.holdings || []);
      } catch (e) {
        this.error = e && e.code === 'functions/not-found' ?
          'This link is no longer valid. The newest thing you received carries a fresh one.' :
          'We couldn’t open this page just now. Please try again in a minute.';
      } finally {
        this.loading = false;
      }
    },
    buildPolicy() {
      const cap = Number.parseInt(this.capChoice, 10);
      const maxPerMonth = Number.isFinite(cap) ? Math.min(31, Math.max(0, cap)) : null;
      const policy = { ...this.policy, maxPerMonth, quietUntil: this.quietUntilMs, constraints: this.constraintLines };
      if (!policy.note || !policy.note.trim()) delete policy.note;
      return policy;
    },
    async savePolicy() {
      this.saving = true;
      this.error = null;
      this.savedAt = null;
      try {
        const { data } = await setRecipientPolicy({ token: this.token, policy: this.buildPolicy() });
        this.applyRecipient(data.recipient);
        this.savedAt = Date.now();
        return true;
      } catch (e) {
        this.error = (e && e.message) || 'We couldn’t save that. Please try again.';
        return false;
      } finally {
        this.saving = false;
      }
    },
    // The one Save: the policy first (saving it is what makes the page
    // theirs), then what they wrote about their taste and the list of what
    // they already have, each only if it changed.
    async save() {
      const taste = { ...this.taste };
      const tasteChanged = this.tasteChanged;
      const ok = await this.savePolicy();
      if (!ok) return;
      if (tasteChanged) {
        this.saving = true;
        try {
          const { data } = await setRecipientTaste({ token: this.token, into: taste.into, enough: taste.enough });
          const t = (data.recipient && data.recipient.taste) || taste;
          this.taste = { into: t.into || '', enough: t.enough || '' };
          this.savedTaste = { ...this.taste };
        } catch (e) {
          this.taste = taste;
          this.savedAt = null;
          this.error = (e && e.message) || 'Your choices saved, but what you wrote about your taste didn’t. Please try again.';
          this.saving = false;
          return;
        }
        this.saving = false;
      }
      if (this.holdingsText.trim() === this.savedHoldingsText.trim()) return;
      this.saving = true;
      try {
        const items = this.holdingsText.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 20);
        const { data } = await setRecipientHoldings({ token: this.token, items });
        this.applyHoldings(data.holdings || []);
      } catch (e) {
        this.savedAt = null;
        this.error = (e && e.message) || 'Your choices saved, but the list of things you have didn’t. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    async claim() {
      this.saving = true;
      this.error = null;
      try {
        const a = this.address;
        const { data } = await claimRecipientAgent({
          token: this.token,
          name: a.name.trim() || undefined,
          ownAddress: {
            street: a.street.trim(), optional: a.optional.trim(), city: a.city.trim(),
            state: a.state.trim(), postalCode: a.postalCode.trim(), country: 'US',
          },
        });
        this.applyRecipient(data.recipient);
        this.savedAt = Date.now();
      } catch (e) {
        this.error = (e && e.message) || 'We couldn’t save that address. Please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.subq { display: block; margin-top: 18px; }
.head { margin: 12px 0 14px; max-width: 22ch; }
.state { margin-top: 18px; max-width: 64ch; }
.err { margin-bottom: 18px; border-color: var(--red); }
.intro { margin: 8px 0 12px; }
.gap { margin-top: 12px; }
.sub { margin: 18px 0 8px; }
.row { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 4px; }
.date { max-width: 16rem; }
.howmuch { line-height: 2.3; margin: 10px 0 0; }
.holdings, .pooling { margin-top: 16px; transition: opacity .15s; }
.steer-choice { margin-top: 16px; transition: opacity .15s; }
.steer-choice.dim { opacity: .55; }
.steer-choice textarea { margin-bottom: 8px; }
.holdings.dim, .pooling.dim { opacity: .55; }
.read { margin-top: 12px; }
.unread { color: var(--red-ink); }
.onfile { margin-bottom: 12px; }
.addr { display: grid; gap: 14px 20px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }
.btn.small { padding: 8px 12px; font-size: .64rem; }
.btn.wide { width: 100%; justify-content: center; }
.react { display: inline-flex; flex-wrap: wrap; gap: 4px; margin-left: 6px; vertical-align: middle; }
.chip.tiny { padding: 2px 7px; font-size: .9rem; }
.fine { margin-top: 26px; }
.recip { display: grid; gap: clamp(22px, 3vw, 36px); margin-top: 32px; }
.mirror.panel { padding: 0; }
.mirror-body { padding: 16px 18px; }
.mirror-text { margin-top: 10px; }
.told { margin: 0; padding-left: 1.1em; }
.told li { margin: 2px 0; }
.mirror-foot { padding: 14px 18px; border-top: 1px solid var(--rule); background: var(--sunk); }
.mirror-foot .xs { margin-top: 10px; }
.saved { color: var(--green); }
code.num { font-size: .85em; background: var(--sunk); padding: 1px 5px; border: 1px solid var(--rule); }
@media (min-width: 980px) {
  .recip { grid-template-columns: minmax(0, 1fr) 330px; align-items: start; }
  .mirror { position: sticky; top: 16px; }
}
@media (max-width: 979px) {
  .mirror { position: sticky; bottom: 0; z-index: 30; order: 2; box-shadow: 0 -7px 20px rgba(40, 26, 10, .14); }
  .mirror-body { max-height: 22vh; overflow: auto; padding: 12px 16px; }
  .mirror-foot { padding: 10px 16px; }
  .mirror-foot .xs { display: none; }
  .recip-fields { order: 1; }
}
</style>
